import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper, Typography, Box, Button, TextField, Grid, Divider,
  CircularProgress, FormControl, Select, MenuItem, Switch, FormControlLabel,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SellerMultiSelect from './shared/SellerMultiSelect';
import OptionMultiSelect from './shared/OptionMultiSelect';
import CategoryMultiSelect from './shared/CategoryMultiSelect';
import { useProductFilterMaster } from './shared/productFilterApiHelpers';
import LocationCascadeSelect from './shared/LocationCascadeSelect';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(1),
    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  sectionIcon: {
    color: '#3f51b5',
    fontSize: 24,
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 400,
    color: '#333',
  },
  sectionSubtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: theme.spacing(3),
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' },
  },
  formLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  formGroup: {
    marginBottom: theme.spacing(2.5),
  },
  multiSelect: {
    backgroundColor: '#f5f5f5',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bdbdbd' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3f51b5' },
  },
  slaDaysInput: {
    width: 160,
    '& .MuiOutlinedInput-root': { backgroundColor: '#fff3e0' },
  },
  priorityInput: {
    width: 140,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  primaryButton: {
    backgroundColor: '#3f51b5',
    color: 'white',
    textTransform: 'none',
    padding: theme.spacing(1.5, 3),
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': { backgroundColor: '#303f9f' },
    '&:disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
  },
}));

export const EMPTY_SLA_RULE_FORM = {
  rule_name: '',
  last_mile_warehouse: '',
  destination_countries: [],
  destination_cities: [],
  category_ids: [],
  sold_by: [],
  seller_ids: [],
  brand_names: [],
  additional_sla: '',
  priority: '0',
  status: true,
};

/**
 * Build POST/PUT body for SLA rules. When omitEmptyArrays is true (create), array fields
 * that are empty are omitted so the API treats them like the Postman samples (no filter).
 */
export const buildSlaRulePayload = (form, { omitEmptyArrays = false } = {}) => {
  const payload = {
    rule_name: form.rule_name.trim(),
    last_mile_warehouse: form.last_mile_warehouse,
    destination_countries: form.destination_countries || [],
    destination_cities: form.destination_cities || [],
    category_ids: form.category_ids || [],
    sold_by: form.sold_by || [],
    seller_ids: form.seller_ids || [],
    brand_names: form.brand_names || [],
    additional_sla: Number(form.additional_sla),
    priority: Number(form.priority) || 0,
    status: Boolean(form.status),
  };
  if (!omitEmptyArrays) return payload;
  return Object.fromEntries(
    Object.entries(payload).filter(([, v]) => !Array.isArray(v) || v.length > 0)
  );
};

const AttributeRuleForm = ({ warehouses, countries, saving, onSubmit, getCountryName }) => {
  const classes = useStyles();
  const [form, setForm] = useState({ ...EMPTY_SLA_RULE_FORM });
  const {
    soldBy: soldByOptions,
    brandName: brandOptions,
    category: categoryTree,
    loading: filterLoading,
    error: filterError,
  } = useProductFilterMaster();

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const countryOptions = useMemo(
    () => (countries || []).map((c) => ({ id: c.code, label: c.name })),
    [countries]
  );

  const isValid =
    form.rule_name.trim() &&
    form.last_mile_warehouse &&
    form.additional_sla !== '' &&
    Number(form.additional_sla) !== 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    const success = await onSubmit(buildSlaRulePayload(form, { omitEmptyArrays: true }));
    if (success) setForm({ ...EMPTY_SLA_RULE_FORM });
  };

  return (
    <Paper className={classes.card}>
      <Box className={classes.sectionHeader}>
        <AddIcon className={classes.sectionIcon} />
        <Typography className={classes.sectionTitle}>Create SLA rule</Typography>
      </Box>
      <Typography className={classes.sectionSubtitle}>
        Rules add extra SLA days to the base ETA when SKU and network attributes match. Evaluated in search-city flow.
        Empty country/city lists mean &quot;match all&quot;. Higher priority is evaluated first.
      </Typography>
      <Divider style={{ marginBottom: 24 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box className={classes.formGroup}>
            <Typography className={classes.formLabel}>Rule name *</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="e.g. DGG UAE export"
              value={form.rule_name}
              onChange={(e) => update('rule_name', e.target.value)}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box className={classes.formGroup}>
            <Typography className={classes.formLabel}>Last mile warehouse *</Typography>
            <FormControl variant="outlined" size="small" className={classes.formControl}>
              <Select
                value={form.last_mile_warehouse}
                onChange={(e) => update('last_mile_warehouse', e.target.value)}
                displayEmpty
                className={classes.multiSelect}
              >
                <MenuItem value=""><em>Select warehouse</em></MenuItem>
                {warehouses.map((wh) => (
                  <MenuItem key={wh.warehouse_id} value={wh.warehouse_id}>
                    {wh.warehouse_name || wh.warehouse_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <OptionMultiSelect
            label="Destination countries"
            placeholder="Search and select countries"
            options={countryOptions}
            values={form.destination_countries}
            onChange={(next) => update('destination_countries', next)}
            emptyText="No countries found"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Box className={classes.formGroup}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.status}
                  onChange={(e) => update('status', e.target.checked)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <LocationCascadeSelect
            selectedCountries={form.destination_countries}
            warehouse={form.last_mile_warehouse}
            selectedCities={form.destination_cities}
            onCitiesChange={(ids) => update('destination_cities', ids)}
            getCountryName={getCountryName}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CategoryMultiSelect
            label="Categories"
            placeholder="Search and select categories"
            tree={categoryTree}
            values={form.category_ids}
            onChange={(next) => update('category_ids', next)}
            loading={filterLoading}
            error={filterError}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <OptionMultiSelect
            label="Sold by"
            placeholder="Search and select sold-by values"
            options={soldByOptions}
            values={form.sold_by}
            onChange={(next) => update('sold_by', next)}
            loading={filterLoading}
            error={filterError}
            emptyText="No sold-by values found"
            loadingText="Loading sold-by values..."
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <OptionMultiSelect
            label="Brand names"
            placeholder="Search and select brands"
            options={brandOptions}
            values={form.brand_names}
            onChange={(next) => update('brand_names', next)}
            loading={filterLoading}
            error={filterError}
            emptyText="No brands found"
            loadingText="Loading brands..."
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SellerMultiSelect
            label="Sellers"
            placeholder="Search and select sellers"
            values={form.seller_ids}
            onChange={(next) => update('seller_ids', next)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Box className={classes.formGroup}>
            <Typography className={classes.formLabel}>Additional SLA (days) *</Typography>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              placeholder="e.g. 2"
              value={form.additional_sla}
              onChange={(e) => update('additional_sla', e.target.value)}
              className={classes.slaDaysInput}
              inputProps={{ min: 0 }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box className={classes.formGroup}>
            <Typography className={classes.formLabel}>Priority</Typography>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              placeholder="0"
              value={form.priority}
              onChange={(e) => update('priority', e.target.value)}
              className={classes.priorityInput}
              inputProps={{ min: 0 }}
            />
          </Box>
        </Grid>
      </Grid>

      <Box className={classes.buttonRow}>
        <Button
          variant="contained"
          className={classes.primaryButton}
          onClick={handleSubmit}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
          disabled={saving || !isValid}
        >
          {saving ? 'Creating...' : 'Create rule'}
        </Button>
      </Box>
    </Paper>
  );
};

export default AttributeRuleForm;
